import { useParams } from '@tanstack/react-router'
import { createContext, useContext, type ReactNode } from 'react'

import { useShadowUserId } from './shadow-context'
import type { CustomerScope } from '@/utils/customer-shadow'

/**
 * Which project the surrounding customer-area screens belong to.
 *
 * Read from here rather than passed down, for the reason the shadow user is:
 * an optional prop made forgetting it silent, and a screen without it read
 * whichever project the server would have picked. There is no project outside
 * a project route, so a reader outside the provider is a mistake rather than a
 * case to handle.
 *
 * It is not the route param read twice: `useRouteProjectId` answers
 * `undefined` off a route that names none, and a scope built on that asks for
 * `customer/projects/undefined`. The throw below is what the provider buys.
 */
const CustomerProjectContext = createContext<number | undefined>(undefined)

export function CustomerProjectProvider({
  projectId,
  children,
}: {
  projectId: number
  children: ReactNode
}) {
  return (
    <CustomerProjectContext.Provider value={projectId}>
      {children}
    </CustomerProjectContext.Provider>
  )
}

/**
 * The project the path names, for the surfaces that sit at or above the
 * provider and so cannot read the context — the scope itself, the switcher in
 * the header, the advisor's shell.
 *
 * Read loosely because those surfaces serve both the customer's own routes and
 * the shadow mirror, which carries a `$userId` ahead of the project.
 */
export function useRouteProjectId(): number | undefined {
  const { projectId } = useParams({ strict: false }) as { projectId?: string }
  const id = Number(projectId)
  return Number.isInteger(id) ? id : undefined
}

/** The open project where there is one. For the few surfaces that span
 *  screens with and without — the dashboard, the shadow shell. */
export function useOptionalCustomerProjectId(): number | undefined {
  return useContext(CustomerProjectContext)
}

/** The open project. Throws outside a project route, which is where every
 *  caller sits. */
export function useCustomerProjectId(): number {
  const projectId = useContext(CustomerProjectContext)
  if (projectId === undefined) {
    throw new Error(
      'useCustomerProjectId outside a CustomerProjectProvider — a customer screen that reads project data belongs under a /customer/project/$projectId route.',
    )
  }
  return projectId
}

/**
 * Which customer's which project the screens underneath read and write — what
 * every project-scoped query and request is addressed by.
 */
export function useCustomerScope(): CustomerScope {
  return {
    shadowUserId: useShadowUserId(),
    projectId: useCustomerProjectId(),
  }
}
